/*
 * A markdown body: one walk over the server's node tree through a **closed** registry (REUSE G1,
 * spec 06 §4). No HTML ever crosses the wire and none is ever produced here — a node type the
 * registry does not know renders a visible placeholder, never markup, so a tree written by a newer
 * server degrades instead of disappearing.
 *
 * Everything a node needs from outside the tree arrives through the render context (context.ts):
 * the names behind a mention, how a link navigates, and how a token a module registered is drawn.
 * Core draws the two kinds it resolves itself — a person and a group — and hands every other kind to
 * the module that registered it; a kind nobody draws is the characters the author typed.
 *
 * A body can reveal its invisible characters (`revealInvisible`, invisible.ts): every leaf that
 * prints what the author wrote then draws a bidi control or a zero-width character as a chip. A body
 * that does not ask draws exactly what it drew before the reveal existed.
 */

import { Fragment, useContext, type MouseEvent, type ReactNode } from "react"
import { safeHref } from "../../routes/safeHref.ts"
import type { Render as ComposerRender } from "../composer/types.ts"
import { t } from "../i18n/index.ts"
import { CodeBlock } from "./CodeBlock.tsx"
import { InsideLink, RevealInvisible, useRenderContext } from "./context.ts"
import { Literal } from "./InvisibleText.tsx"
import type { MarkdownNode, Render } from "./nodes.ts"
import { RenderFlag } from "./nodes.ts"
import "./render.css"

/** `data-md="<start>-<end>"`: what a selection copy maps a DOM range onto (A28). */
function blockAttrs(node: MarkdownNode): { "data-md"?: string } {
	const src = "src" in node ? node.src : undefined
	if (!src || src.length < 2) return {}
	return { "data-md": `${src[0]}-${src[1]}` }
}

function renderChildren(node: MarkdownNode, key: string): ReactNode {
	const kids = "children" in node ? node.children : undefined
	return kids ? renderNodes(kids, key) : null
}

function Link({
	node,
	keyPrefix,
}: {
	node: Extract<MarkdownNode, { type: "link" }>
	keyPrefix: string
}) {
	const { navigate } = useRenderContext()
	const href = safeHref(node.href)
	const label = node.children ? renderNodes(node.children, keyPrefix) : <Literal text={node.href} />
	// A refused scheme is not a link: the text stays, the anchor does not appear.
	if (href === null) return <span className="tb-link-refused">{label}</span>

	const internal = href.startsWith("/")
	const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
		if (!internal || !navigate || event.metaKey || event.ctrlKey || event.shiftKey) return
		event.preventDefault()
		navigate(href)
	}
	return (
		<>
			<a
				href={href}
				title={node.title}
				onClick={onClick}
				{...(internal ? {} : { target: "_blank", rel: "noreferrer noopener" })}
			>
				<InsideLink value={true}>{label}</InsideLink>
			</a>
			{node.hrefHost ? <span className="tb-host-tell">{` (${node.hrefHost})`}</span> : null}
		</>
	)
}

/**
 * A permalink chip. The address it carries belongs to the module whose messages it points at, which
 * answers it through `permalinkHref`; without an answer the chip is a label, never a door that leads
 * nowhere.
 */
function Permalink({ node }: { node: Extract<MarkdownNode, { type: "permalink" }> }) {
	const { permalinkHref, navigate } = useRenderContext()
	const label = t("render.permalink", { seq: node.perma.seq })
	const target = permalinkHref?.(node.perma) ?? null
	const href = target === null ? null : safeHref(target)
	if (href === null) return <span className="tb-permalink">{label}</span>
	return (
		<a
			className="tb-permalink"
			href={href}
			onClick={(event) => {
				if (!navigate) return
				event.preventDefault()
				navigate(href)
			}}
		>
			{label}
		</a>
	)
}

/*
 * Every resolved token is a `.tb-tag` (ui/ui.css): a person filled, a group outlined. The
 * `tb-mention*` classes beside it carry no paint of their own; they stay because the tests and the
 * copy flavours locate the chips by them.
 */
function MentionChip({ node }: { node: Extract<MarkdownNode, { type: "mention" }> }) {
	const { names, groups, openProfile, mentionKinds } = useRenderContext()
	const insideLink = useContext(InsideLink)
	const { kind, handle, id } = node.mention
	const token = `@${handle}`

	if (kind !== "user" && kind !== "group") {
		// A token a module registered: drawn by that module, and the characters the author typed on
		// a surface that does not know it. An unresolved `@handle` (kind "") has no drawing either.
		const drawing = mentionKinds?.[kind]
		return drawing ? <>{drawing(node.mention, { insideLink })}</> : <>{token}</>
	}
	// An unresolved token is not a chip: it is what the author typed, and nothing more.
	if (!id) return <>{token}</>

	if (kind === "group") {
		const group = groups[id]
		const members = (group?.memberIds ?? [])
			.map((memberId) => names[memberId] ?? "")
			.filter((name) => name !== "")
			.join(", ")
		return (
			<span
				className="tb-tag tb-tag--group tb-mention tb-mention--group"
				title={t("render.mentionGroup", { handle, members })}
			>
				{token}
			</span>
		)
	}
	if (openProfile === undefined) {
		return (
			<span className="tb-tag tb-mention tb-mention--user" title={names[id] ?? token}>
				{token}
			</span>
		)
	}
	return (
		<button
			type="button"
			className="tb-tag tb-mention tb-mention--user"
			title={names[id] ?? token}
			onClick={() => openProfile(id)}
		>
			{token}
		</button>
	)
}

type NodeRenderer = (node: MarkdownNode, key: string) => ReactNode

/**
 * The five blocks whose last line can carry an inline that is not the author's: "(edited)" belongs at
 * the end of the body's last line and never on a line of its own (D-05 D3, X-A1 P8, X-B1 D9). Each is
 * drawn either from its own children — which is what the registry asks for — or from a child list
 * `renderWithTrailing` has already put that inline into.
 *
 * They are one function because the recursion needs exactly this set: a paragraph and a heading hold
 * the inline on their own line, and a quote, a list and a list item hand it to their last child.
 */
function carrier(node: MarkdownNode, key: string, children?: ReactNode): ReactNode {
	const kids = children ?? renderChildren(node, key)
	switch (node.type) {
		case "paragraph":
			return (
				<p key={key} {...blockAttrs(node)}>
					{kids}
				</p>
			)
		case "heading": {
			// The view keeps exactly one h1 (the conversation name), so a message heading starts at h3.
			const Tag = `h${Math.min(Math.max(node.level, 1) + 2, 6)}` as "h3" | "h4" | "h5" | "h6"
			return (
				<Tag key={key} {...blockAttrs(node)}>
					{kids}
				</Tag>
			)
		}
		case "quote":
			return (
				<blockquote key={key} {...blockAttrs(node)}>
					{kids}
				</blockquote>
			)
		case "list": {
			// `tb-md-list`, not `tb-list`: that one is the windowed list's scroller (core/list/list.css), and a
			// markdown list wearing it inherited `overflow-y: auto` and became a scrollable region no
			// keyboard could reach.
			const className = node.tight ? "tb-md-list tb-md-list--tight" : "tb-md-list"
			return node.ordered ? (
				<ol key={key} className={className} start={node.start} {...blockAttrs(node)}>
					{kids}
				</ol>
			) : (
				<ul key={key} className={className} {...blockAttrs(node)}>
					{kids}
				</ul>
			)
		}
		case "item":
			return (
				<li key={key} className={node.checked === undefined ? undefined : "tb-task-item"}>
					{node.checked === undefined ? null : (
						<input type="checkbox" checked={node.checked} disabled readOnly aria-hidden="true" />
					)}
					{kids}
				</li>
			)
		default:
			return null
	}
}

/** The types `carrier` draws, which is the set the trailing inline can be handed down through. */
const CARRIERS: ReadonlySet<string> = new Set(["paragraph", "heading", "quote", "list", "item"])

/** The node types drawn as an inline run: a line of words a trailing inline simply joins. */
const INLINES: ReadonlySet<string> = new Set([
	"text",
	"em",
	"strong",
	"strike",
	"codespan",
	"link",
	"break",
	"permalink",
	"image",
	"math",
	"mention",
	"emoji",
])

/** The blocks that stand below the compact row's floated header rather than beside it. */
const CLEARS_HEADER: ReadonlySet<string> = new Set(["list", "quote", "code", "table"])

/**
 * Whether a body opens on the line before it rather than under it — what a compact row needs to
 * know, because its header is a float and its trailing dot separates the time from words that are on
 * the same line (X-B2 B2-12). A paragraph or a heading flows beside the name; a list, a quote, a
 * fence or a table clears it (the `clear` rule of chat's compact row is the other half of this), and a
 * body of nothing — an attachment-only message — has no line at all.
 */
export function leadsBesideHeader(render: Render | undefined, source: string): boolean {
	const first = render?.nodes?.[0]
	if (first === undefined) return source !== ""
	return !CLEARS_HEADER.has(first.type)
}

/**
 * The registry. It is closed by construction: a type that is not a key here cannot render, and the
 * lookup is by string so that a tree from a newer server hits the placeholder instead of `undefined`.
 */
const RENDERERS: Readonly<Record<string, NodeRenderer | undefined>> = {
	paragraph: (node, key) => carrier(node, key),
	heading: (node, key) => carrier(node, key),
	quote: (node, key) => carrier(node, key),
	list: (node, key) => carrier(node, key),
	item: (node, key) => carrier(node, key),
	code: (node, key) =>
		node.type === "code" ? <CodeBlock key={key} text={node.text} lang={node.lang} /> : null,
	// A named, focusable region is the technique a keyboard user needs to scroll a wide table
	// ([25] G1) — the tabindex is deliberate and the label is what makes the stop meaningful.
	table: (node, key) => (
		<div
			key={key}
			className="tb-table-scroll"
			role="region"
			// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
			tabIndex={0}
			aria-label={t("render.table")}
		>
			<table {...blockAttrs(node)}>
				<tbody>{renderChildren(node, key)}</tbody>
			</table>
		</div>
	),
	// The header row markdown wrote is a row of header cells, so a screen reader hears the column a
	// value belongs to; the cell renderer itself never knows its row, which is why the row draws them.
	row: (node, key) =>
		node.type === "row" && node.head === true ? (
			<tr key={key}>
				{(node.children ?? []).map((cell, index) => (
					<th key={`${key}.${String(index)}`} scope="col">
						{renderChildren(cell, `${key}.${String(index)}`)}
					</th>
				))}
			</tr>
		) : (
			<tr key={key}>{renderChildren(node, key)}</tr>
		),
	cell: (node, key) => <td key={key}>{renderChildren(node, key)}</td>,
	rule: (node, key) => <hr key={key} {...blockAttrs(node)} />,
	text: (node, key) =>
		node.type === "text" ? (
			<span key={key}>
				<Literal text={node.text} />
			</span>
		) : null,
	em: (node, key) => <em key={key}>{renderChildren(node, key)}</em>,
	strong: (node, key) => <strong key={key}>{renderChildren(node, key)}</strong>,
	strike: (node, key) => <s key={key}>{renderChildren(node, key)}</s>,
	codespan: (node, key) =>
		node.type === "codespan" ? (
			<code key={key} className="tb-codespan">
				<Literal text={node.text} />
			</code>
		) : null,
	link: (node, key) =>
		node.type === "link" ? <Link key={key} node={node} keyPrefix={key} /> : null,
	break: (_node, key) => <br key={key} />,
	permalink: (node, key) =>
		node.type === "permalink" ? <Permalink key={key} node={node} /> : null,
	// An image is drawn as a link, never as an <img>: no message body ever makes the browser fetch
	// from a third-party host (Q23 a).
	image: (node, key) => {
		if (node.type !== "image") return null
		const href = safeHref(node.href)
		const label = <Literal text={t("render.image", { name: node.alt ?? node.href })} />
		if (href === null) return <span key={key}>{label}</span>
		return (
			<a key={key} href={href} target="_blank" rel="noreferrer noopener" title={node.title}>
				{label}
			</a>
		)
	},
	// Reserved and never emitted in v1: show the source, do not pretend to typeset it.
	math: (node, key) =>
		node.type === "math" ? (
			<code key={key} className="tb-codespan">
				<Literal text={node.latex} />
			</code>
		) : null,
	mention: (node, key) => (node.type === "mention" ? <MentionChip key={key} node={node} /> : null),
	emoji: (node, key) =>
		node.type === "emoji" ? (
			<span key={key} className="tb-emoji" title={node.emoji.name}>
				{node.emoji.char}
			</span>
		) : null,
}

function renderNodes(nodes: readonly MarkdownNode[], keyPrefix = "n"): ReactNode[] {
	return nodes.map((node, index) => {
		const key = `${keyPrefix}.${index}`
		const renderer = RENDERERS[node.type]
		if (!renderer) {
			return (
				<span key={key} className="tb-unknown-node" data-node={node.type}>
					{t("render.unknownNode")}
				</span>
			)
		}
		return renderer(node, key)
	})
}

/**
 * `nodes` with `trailing` at the end of the last line words are on, or `null` when no line in the
 * last block can hold an inline.
 *
 * The recursion is the whole rule: a run of inlines takes it straight after the last of them, and a
 * block that carries lines hands it down to its own last child — so "(edited)" lands in the last list
 * item's last line, on the heading's line, inside the quote's last paragraph. A code fence, a table
 * and a rule refuse it, and that refusal travels up: a body ending in one of those three, or in a
 * quote or a list ending in one, is the one case that puts the inline on a line of its own
 * (`renderBlocks`).
 */
function renderWithTrailing(
	nodes: readonly MarkdownNode[],
	keyPrefix: string,
	trailing: ReactNode,
): ReactNode[] | null {
	const index = nodes.length - 1
	const last = nodes[index]
	if (last === undefined) return null
	if (INLINES.has(last.type)) {
		return [...renderNodes(nodes, keyPrefix), <Fragment key="trailing">{trailing}</Fragment>]
	}
	if (!CARRIERS.has(last.type)) return null
	const key = `${keyPrefix}.${index}`
	const inner = renderWithTrailing("children" in last ? (last.children ?? []) : [], key, trailing)
	if (inner === null) return null
	return [...renderNodes(nodes.slice(0, -1), keyPrefix), carrier(last, key, inner)]
}

/**
 * The blocks of a body with `trailing` where it belongs: at the end of the last line of words, or —
 * under a code fence or a table, which cannot hold an inline — on a line of its own, set to the end
 * of the block above it (X-B1 D9).
 */
function renderBlocks(nodes: readonly MarkdownNode[], trailing: ReactNode): ReactNode[] {
	const placed = renderWithTrailing(nodes, "n", trailing)
	if (placed !== null) return placed
	return [
		...renderNodes(nodes),
		<p key="trailing" className="tb-body__trailing">
			{trailing}
		</p>,
	]
}

/**
 * The composer's preview pane, in the shape core's `ComposerProps.renderPreview` asks for. The
 * `Render` that crosses that seam is the composer's opaque one — the composer never names a node
 * type — and this is where it becomes the tree the registry draws.
 */
export function MarkdownPreview({ render }: { render: ComposerRender }) {
	return <MarkdownBody render={render as unknown as Render} source="" />
}

export interface MarkdownBodyProps {
	render?: Render | undefined
	/** The canonical markdown, shown as plain text when no tree arrived (a degraded render). */
	source: string
	/**
	 * An inline that belongs to the end of the body's last line — the row's "(edited)". It is drawn
	 * inside the last line that holds words, however deep that line sits, and falls to a line of its
	 * own only under a block that can hold no inline at all (`renderBlocks`).
	 */
	trailing?: ReactNode
	/**
	 * Draw the invisible characters of what the author wrote as chips (invisible.ts): on for text a
	 * reader must be able to trust exactly — an AI chat's prose — and off where people write to each
	 * other, which is what a body draws when it is not asked.
	 */
	revealInvisible?: boolean | undefined
}

export function MarkdownBody({
	render,
	source,
	trailing,
	revealInvisible = false,
}: MarkdownBodyProps) {
	const body = drawBody(render, source, trailing)
	return revealInvisible ? <RevealInvisible value={true}>{body}</RevealInvisible> : body
}

function drawBody(render: Render | undefined, source: string, trailing: ReactNode): ReactNode {
	// A message that is only attachments has an empty tree, and a row stored before the server
	// promised a list carries `nodes: null`; "no tree", "no nodes" and "an empty list" are one case
	// here, and a body of nothing is nothing rather than a crash — unless the row has a trailing
	// inline to place, which then stands alone where the last line would have been.
	if (!render?.nodes || render.nodes.length === 0) {
		if (source === "") return trailing === undefined ? null : <p className="tb-body">{trailing}</p>
		return (
			<p className="tb-body-plain">
				<Literal text={source} />
				{trailing}
			</p>
		)
	}
	const jumbo = (render.flags & RenderFlag.jumbo) !== 0
	const truncated = (render.flags & RenderFlag.truncated) !== 0
	return (
		<div className={jumbo ? "tb-body tb-body--jumbo" : "tb-body"}>
			{trailing === undefined ? renderNodes(render.nodes) : renderBlocks(render.nodes, trailing)}
			{truncated ? <p className="tb-body-truncated">{t("render.truncated")}</p> : null}
		</div>
	)
}
